// src/pages/PostContentPage/PostContentPage.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaUpload, FaCheck, FaTimes, FaClock, FaMapMarkerAlt,
  FaCheckCircle, FaTimesCircle, FaInfo, FaEye, FaArrowRight,
  FaLock, FaGlobe, FaUsers, FaTag, FaLayerGroup,
} from "react-icons/fa";
import { CreatePost } from "./utils/CreatePost";

// ==================== CONSTANTS ====================

const EVENT_TYPES = [
  { value: "festival",    label: "🎉 Festival"         },
  { value: "concert",     label: "🎵 Concert"          },
  { value: "sports",      label: "🏆 Sports"           },
  { value: "cultural",    label: "🎭 Cultural"         },
  { value: "religious",   label: "🙏 Religious"        },
  { value: "community",   label: "🏘️ Community"        },
  { value: "educational", label: "📚 Educational"      },
  { value: "personal",    label: "👤 Personal"         },
  { value: "corporate",   label: "🏢 Corporate"        },
  { value: "charity",     label: "❤️ Charity"          },
  { value: "other",       label: "📌 Other"            },
];

const EVENT_SEGMENTS = [
  { value: "",             label: "— No specific segment —" },
  { value: "current_month",label: "📅 Current Month"        },
  { value: "last_month",   label: "📆 Last Month"           },
  { value: "worldwide",    label: "🌍 Worldwide"            },
  { value: "upcoming",     label: "🔮 Upcoming"             },
];

const EVENT_CATEGORIES = [
  { value: "personal",  label: "👤 Personal",  desc: "Private life moments & milestones"  },
  { value: "religious", label: "🙏 Religious", desc: "Faith-based & spiritual events"       },
  { value: "social",    label: "🤝 Social",    desc: "Community & social gatherings"        },
  { value: "cultural",  label: "🎨 Cultural",  desc: "Arts, heritage & cultural events"     },
  { value: "national",  label: "🏳️ National",  desc: "Public holidays & national events"   },
];

// ==================== SHARED MICRO-STYLES ====================

const inputStyle = {
  flex: 1, padding: "12px 15px", borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.15)",
  backgroundColor: "rgba(255,255,255,0.05)",
  color: "white", fontSize: "0.95rem", outline: "none", boxSizing: "border-box",
};

const selectStyle = {
  ...inputStyle,
  width: "100%", cursor: "pointer",
  appearance: "none", WebkitAppearance: "none",
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,0.4)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
  paddingRight: "36px",
};

const labelStyle = {
  color: "white", fontWeight: 600, fontSize: "0.9rem",
  display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
  height: "60px"
};

const sectionStyle = { marginBottom: 30 };

const submitBtnStyle = {
  padding: "12px 24px", borderRadius: 8, border: "none",
  background: "#2196F3", color: "white", fontWeight: 600,
  cursor: "pointer", transition: "all 0.3s ease",
  display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap",
};

const validBg     = (ok) => ok ? "rgba(76,175,80,0.1)"            : "rgba(244,67,54,0.1)";
const validBorder = (ok) => ok ? "1px solid rgba(76,175,80,0.3)"  : "1px solid rgba(244,67,54,0.3)";
const validBadge  = (ok) => ok ? "#4CAF50"                         : "#f44336";

// ==================== COMPONENT ====================

const PostContentPage = () => {

  // ── Core form state ───────────────────────────────────────────────────
  const [mediaFiles,         setMediaFiles]         = useState([]);
  const [description,        setDescription]        = useState("");
  const [selectedGroup,      setSelectedGroup]      = useState("");
  const [location,           setLocation]           = useState("");
  const [timestamp,          setTimestamp]          = useState(new Date().toISOString().slice(0, 16));
  const [isMember,           setIsMember]           = useState(false);
  const [isSubmitting,       setIsSubmitting]       = useState(false);
  const [submitError,        setSubmitError]        = useState(null);
  const [submitSuccess,      setSubmitSuccess]      = useState(false);
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const [showQualityReview,  setShowQualityReview]  = useState(false);
  const [qualityReviews,     setQualityReviews]     = useState([]);
  const [contentQuality,     setContentQuality]     = useState({
    hasMedia: false, hasDescription: false, hasLocation: false,
    mediaQualityOk: false, descriptionLengthOk: false,
    hasEventType: false, overallScore: 0,
  });

  // ── NEW: Event metadata ───────────────────────────────────────────────
  const [eventType,     setEventType]     = useState("");           // mandatory
  const [eventSegment,  setEventSegment]  = useState("");           // optional
  const [eventCategory, setEventCategory] = useState("");           // personal | religious | social | cultural | national

  // ── NEW: Visibility & audience ───────────────────────────────────────
  const [isPrivate,    setIsPrivate]    = useState(false);          // private flag
  const [societyInfo,  setSocietyInfo]  = useState("");             // society name/id
  const [targetGroups, setTargetGroups] = useState([]);             // selected group ids
  const [groupInput,   setGroupInput]   = useState("");             // add-group text input

  // ── Parameter validation (userid + groupid only — profileid removed) ─
  const [validatedUserid,  setValidatedUserid]  = useState(null);
  const [validatedGroupid, setValidatedGroupid] = useState(null);
  const [useridInput,      setUseridInput]       = useState("");
  const [groupidInput,     setGroupidInput]      = useState("");
  const [allParametersValid, setAllParametersValid] = useState(false);

  const fileInputRef = useRef(null);
  const navigate     = useNavigate();
  const { userid, groupid } = useParams();   // ← profileid removed from route
  const [groupidSkipped, setGroupidSkipped] = useState(false);
  const validCount = [validatedUserid, validatedGroupid || groupidSkipped ? "ok" : null]
  .filter(Boolean).length;

  // ==================== EFFECTS ====================

  useEffect(() => {
    if (userid && userid.trim().length > 0) setValidatedUserid(userid);
    else setValidatedUserid(null);

    if (groupid && groupid.trim().length > 0) setValidatedGroupid(groupid);
    else setValidatedGroupid(null);
  }, [userid, groupid]);

  useEffect(() => {
    const isValid = !!(validatedUserid && validatedGroupid);
    setAllParametersValid(isValid);
    if (validatedGroupid) { setSelectedGroup(validatedGroupid); setIsMember(true); }
  }, [validatedUserid, validatedGroupid]);

  useEffect(() => {
    const allComplete = mediaFiles.every(m => m.uploadStatus === "success" || m.uploadStatus === "error");
    setAllUploadsComplete(allComplete && mediaFiles.length > 0);
  }, [mediaFiles]);

  // Quality score — now includes eventType (mandatory, worth 20pts of 120 → normalised to 100)
  useEffect(() => {
    const hasMedia            = mediaFiles.length > 0;
    const hasDescription      = description.trim().length > 0;
    const hasLocation         = location.trim().length > 0;
    const mediaQualityOk      = mediaFiles.every(m => m.uploadStatus !== "error");
    const descriptionLengthOk = description.trim().length >= 10;
    const hasEventType        = eventType.trim().length > 0;

    const raw = [
      hasMedia            ? 20 : 0,
      hasDescription      ? 20 : 0,
      hasLocation         ? 15 : 0,
      mediaQualityOk      ? 15 : 0,
      descriptionLengthOk ? 15 : 0,
      hasEventType        ? 15 : 0,
    ].reduce((a, b) => a + b, 0);

    setContentQuality({
      hasMedia, hasDescription, hasLocation,
      mediaQualityOk, descriptionLengthOk, hasEventType,
      overallScore: raw,
    });
  }, [mediaFiles, description, location, eventType]);

  // ==================== PARAM VALIDATION HANDLERS ====================

  const handleUseridSubmit = () => {
    if (!useridInput.trim()) { alert("Please enter a valid User ID"); return; }
    setValidatedUserid(useridInput.trim());
  };

  const handleGroupidSubmit = () => {
    if (!groupidInput.trim()) { alert("Please enter a valid Group ID"); return; }
    setValidatedGroupid(groupidInput.trim());
    setSelectedGroup(groupidInput.trim());
    setIsMember(true);
  };

  // ==================== GROUP TAGS ====================

  const addTargetGroup = () => {
    const val = groupInput.trim();
    if (!val || targetGroups.includes(val)) return;
    setTargetGroups(prev => [...prev, val]);
    setGroupInput("");
  };

  const removeTargetGroup = (g) => setTargetGroups(prev => prev.filter(x => x !== g));

  // ==================== UPLOAD SIMULATION ====================

  const simulateUpload = async (mediaId, file) => {
    const totalBytes = file.size;
    const CHUNK      = 1024 * 1024;
    let   uploaded   = 0;
    const start      = Date.now();

    while (uploaded < totalBytes) {
      uploaded += Math.min(CHUNK, totalBytes - uploaded);
      await new Promise(r => setTimeout(r, 300));
      const elapsed  = (Date.now() - start) / 1000;
      const speed    = uploaded / elapsed;
      const progress = Math.round((uploaded / totalBytes) * 100);
      setMediaFiles(prev => prev.map(m =>
        m.id === mediaId
          ? { ...m, uploadStatus: "uploading", uploadProgress: progress, uploadedBytes: uploaded,
              totalBytes, uploadSpeed: speed,
              estimatedTimeRemaining: Math.ceil((totalBytes - uploaded) / speed) }
          : m
      ));
    }

    for (const step of ["processing", "sharpening", "optimizing"]) {
      setMediaFiles(prev => prev.map(m => m.id === mediaId ? { ...m, processingStatus: step } : m));
      await new Promise(r => setTimeout(r, 800));
    }

    setMediaFiles(prev => prev.map(m =>
      m.id === mediaId ? { ...m, uploadStatus: "success", processingStatus: "completed" } : m
    ));
    generateQualityReview(mediaId, file);
  };

  const generateQualityReview = (mediaId, file) => {
    setQualityReviews(prev => [...prev, {
      mediaId,
      originalSize:  file.size,
      optimizedSize: Math.round(file.size * 0.85),
      qualityMetrics: {
        sharpness:     Math.round(70 + Math.random() * 30),
        contrast:      Math.round(65 + Math.random() * 35),
        brightness:    Math.round(60 + Math.random() * 40),
        colorVibrancy: Math.round(70 + Math.random() * 30),
      },
      changes: [
        "Sharpness enhanced by 15%", "Contrast optimized",
        "Color profiles normalized", "EXIF data preserved",
        "Compression applied (15% reduction)",
      ],
      recommendation: "excellent",
    }]);
  };

  const handleMediaUpload = (e) => {
    const files = e.currentTarget.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      const isImage = file.type.startsWith("image/");
      const isVideo = file.type.startsWith("video/");
      if (!isImage && !isVideo) { console.error("Invalid file type"); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const mediaId  = `${Date.now()}-${Math.random()}`;
        setMediaFiles(prev => [...prev, {
          id: mediaId, file, preview: ev.target.result,
          type: isImage ? "image" : "video",
          uploadStatus: "pending", uploadProgress: 0,
          uploadedBytes: 0, totalBytes: file.size,
          uploadSpeed: 0, estimatedTimeRemaining: 0,
        }]);
        simulateUpload(mediaId, file);
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveMedia = (id) => {
    setMediaFiles(prev     => prev.filter(m => m.id !== id));
    setQualityReviews(prev => prev.filter(r => r.mediaId !== id));
  };

  // ==================== FORMATTERS ====================

  const formatBytes = (b) => {
    if (b === 0) return "0 Bytes";
    const k = 1024, s = ["Bytes","KB","MB","GB"], i = Math.floor(Math.log(b) / Math.log(k));
    return Math.round((b / Math.pow(k, i)) * 100) / 100 + " " + s[i];
  };

  const formatTime = (s) => {
    if (s < 60)   return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.floor(s/60)}m ${Math.round(s%60)}s`;
    return `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;
  };

  // ==================== SUBMIT ====================

  const handleSubmitPost = async () => {
    try {
      setSubmitError(null);
      setIsSubmitting(true);

      if (!validatedUserid || !selectedGroup)
        throw new Error("Missing required identifiers (userid, groupid)");
      if (!isMember)
        throw new Error("You must be a member of this group to post");
      if (!eventType.trim())
        throw new Error("Event type is required");
      if (mediaFiles.length === 0)
        throw new Error("Please upload at least one media file");
      if (!description.trim())
        throw new Error("Please add a description");

      const mediasToUpload = mediaFiles
        .filter(m => m.uploadStatus === "success")
        .map(m => ({ file: m.file, type: m.type }));

      if (mediasToUpload.length === 0)
        throw new Error("No successfully uploaded media files");

      await CreatePost({
        userid:        validatedUserid,
        groupid:       selectedGroup,
        descriptions:  description,
        location,
        timestamp,
        medias:        mediasToUpload,
        // ── new fields ──
        eventType,
        eventSegment:  eventSegment  || null,
        eventCategory: eventCategory || null,
        isPrivate,
        societyInfo:   societyInfo   || null,
        targetGroups:  targetGroups.length > 0 ? targetGroups : null,
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        setMediaFiles([]); setDescription(""); setLocation("");
        setTimestamp(new Date().toISOString().slice(0, 16));
        setEventType(""); setEventSegment(""); setEventCategory("");
        setIsPrivate(false); setSocietyInfo(""); setTargetGroups([]);
        setSubmitSuccess(false);
        navigate(`/passion-hubs/${validatedUserid}`);
      }, 2000);

    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to post content");
      console.error("Error submitting post:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==================== DERIVED ====================

  const canSubmit = isMember && mediaFiles.length > 0 && allUploadsComplete && !!eventType.trim();

  const mainContainerStyle  = { display: "flex", flexDirection: "column", width: "100%", minHeight: "100vh", backgroundColor: "rgb(20,20,20)" };
  const pageContentStyle    = { padding: "40px", maxWidth: 900, margin: "0 auto", width: "100%", boxSizing: "border-box" };
  const formContainerStyle  = { background: "var(--card-bg, rgb(4,4,4))", borderRadius: 15, padding: 30, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" };

  // ==================== PARAM VALIDATION SCREEN ====================

  // ── Update the allParametersValid effect ──
useEffect(() => {
  // Valid when: userid confirmed AND (groupid confirmed OR explicitly skipped)
  const isValid = validatedUserid && (validatedGroupid || groupidSkipped);
  setAllParametersValid(!!isValid);

  if (validatedGroupid) {
    setSelectedGroup(validatedGroupid);
    setIsMember(true);
  }

  // When skipped: clear group-related state
  if (groupidSkipped && !validatedGroupid) {
    setSelectedGroup("");
    setIsMember(false);      // post won't require group membership check
  }
}, [validatedUserid, validatedGroupid, groupidSkipped]);

  if (!allParametersValid) {
    const validCount = [validatedUserid, validatedGroupid].filter(Boolean).length;
    // ── Add this new state alongside your existing ones ──
    const StepRow = ({ step, label, validated, input, setInput, onSubmit, placeholder, disabled = false }) => (
      <div style={{
        background: validBg(validated), border: validBorder(validated),
        borderRadius: 10, padding: 20, marginBottom: 25,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        transition: "opacity 0.2s",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 15 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: validBadge(validated), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: "bold" }}>
              {step}
            </div>
            <div>
              <div style={{ color: "white", fontWeight: 600, fontSize: "1.1rem" }}>{label}</div>
              <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
                {validated ? `Verified: ${validated}` : "Required information"}
              </div>
            </div>
          </div>
          {validated && <FaCheck style={{ color: "#4CAF50", fontSize: "1.5rem" }} />}
        </div>
        {!validated && (
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="text" value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={placeholder} style={inputStyle}
              onKeyPress={e => e.key === "Enter" && onSubmit()}
            />
            <button
              onClick={onSubmit} style={submitBtnStyle}
              onMouseEnter={e => { e.currentTarget.style.background = "#1976D2"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#2196F3"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              Submit <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    );

    return (
        <div style={mainContainerStyle}>
          <div style={pageContentStyle}>
            <div style={formContainerStyle}>
              <h1 style={{ color: "white", fontSize: "2rem", marginBottom: 30, textAlign: "center" }}>
                Add Event Content
              </h1>
              <p style={{ color: "rgba(255,255,255,0.7)", textAlign: "center", marginBottom: 40 }}>
                Please provide the required information to proceed
              </p>
      
              {/* ── Step 1: User ID (Required) ── */}
              <StepRow
                step={1}
                label="User ID"
                validated={validatedUserid}
                input={useridInput}
                setInput={setUseridInput}
                onSubmit={handleUseridSubmit}
                placeholder="Enter your User ID"
              />
      
              {/* ── Step 2: Group ID (Optional) ── */}
              <div
                style={{
                  background:    validatedGroupid
                    ? "rgba(76,175,80,0.1)"
                    : groupidSkipped
                    ? "rgba(255,152,0,0.07)"
                    : "rgba(255,255,255,0.03)",
                  border: validatedGroupid
                    ? "1px solid rgba(76,175,80,0.3)"
                    : groupidSkipped
                    ? "1px solid rgba(255,152,0,0.25)"
                    : "1px solid rgba(255,255,255,0.1)",
                  borderRadius:  10,
                  padding:       20,
                  marginBottom:  25,
                  opacity:       !validatedUserid ? 0.5 : 1,
                  pointerEvents: !validatedUserid ? "none" : "auto",
                  transition:    "opacity 0.2s, background 0.2s, border 0.2s",
                }}
              >
                {/* Row header */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: validatedGroupid || groupidSkipped ? 0 : 15 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {/* Step badge */}
                    <div style={{
                      width: 30, height: 30, borderRadius: "50%",
                      background: validatedGroupid
                        ? "#4CAF50"
                        : groupidSkipped
                        ? "#FF9800"
                        : "rgba(255,255,255,0.15)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "white", fontWeight: "bold", fontSize: "1rem",
                      transition: "background 0.2s",
                    }}>
                      {validatedGroupid ? <FaCheck /> : groupidSkipped ? "–" : "2"}
                    </div>
      
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ color: "white", fontWeight: 600, fontSize: "1.1rem" }}>
                          Group ID
                        </span>
                        {/* Optional pill */}
                        <span style={{
                          fontSize: "0.7rem", fontWeight: 600,
                          padding: "2px 8px", borderRadius: 20,
                          background: "rgba(255,152,0,0.15)",
                          border:     "1px solid rgba(255,152,0,0.3)",
                          color:      "#FF9800",
                          letterSpacing: "0.04em",
                        }}>
                          OPTIONAL
                        </span>
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem", marginTop: 2 }}>
                        {validatedGroupid
                          ? `Verified: ${validatedGroupid}`
                          : groupidSkipped
                          ? "Skipped — no group selected"
                          : "Link this post to a group (can be skipped)"}
                      </div>
                    </div>
                  </div>
      
                  {/* Right badge */}
                  {validatedGroupid && (
                    <FaCheck style={{ color: "#4CAF50", fontSize: "1.4rem" }} />
                  )}
                  {groupidSkipped && !validatedGroupid && (
                    <span style={{ fontSize: "1.3rem" }}>⏭️</span>
                  )}
                </div>
      
                {/* Input row — only show if not yet validated or skipped */}
                {!validatedGroupid && !groupidSkipped && (
                  <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                    {/* Text input */}
                    <input
                      type="text"
                      value={groupidInput}
                      onChange={(e) => setGroupidInput(e.target.value)}
                      placeholder="Enter your Group ID"
                      style={inputStyle}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleGroupidSubmit();
                      }}
                    />
      
                    {/* Submit button */}
                    <button
                      onClick={handleGroupidSubmit}
                      style={{
                        ...submitBtnStyle,
                        background: "#2196F3",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background  = "#1976D2";
                        e.currentTarget.style.transform   = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background  = "#2196F3";
                        e.currentTarget.style.transform   = "translateY(0)";
                      }}
                    >
                      Submit <FaArrowRight />
                    </button>
                  </div>
                )}
      
                {/* Undo skip / undo verify links */}
                {(groupidSkipped || validatedGroupid) && (
                  <button
                    onClick={() => {
                      setGroupidSkipped(false);
                      setValidatedGroupid(null);
                      setGroupidInput("");
                    }}
                    style={{
                      marginTop:   10,
                      background:  "transparent",
                      border:      "none",
                      color:       "rgba(255,255,255,0.35)",
                      fontSize:    "0.8rem",
                      cursor:      "pointer",
                      padding:     0,
                      textDecoration: "underline",
                    }}
                  >
                    ← Change Group ID
                  </button>
                )}
              </div>
      
              {/* ── Skip Group ID Button ── */}
              {validatedUserid && !validatedGroupid && !groupidSkipped && (
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 30 }}>
                  <button
                    onClick={() => setGroupidSkipped(true)}
                    style={{
                      display:      "flex",
                      alignItems:   "center",
                      gap:          8,
                      padding:      "10px 24px",
                      borderRadius: 8,
                      border:       "1px solid rgba(255,152,0,0.35)",
                      background:   "rgba(255,152,0,0.08)",
                      color:        "#FF9800",
                      fontSize:     "0.92rem",
                      fontWeight:   600,
                      cursor:       "pointer",
                      transition:   "background 0.2s, transform 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,152,0,0.15)";
                      e.currentTarget.style.transform  = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255,152,0,0.08)";
                      e.currentTarget.style.transform  = "translateY(0)";
                    }}
                  >
                    ⏭️ &nbsp;Skip — I don't have a Group ID
                  </button>
                </div>
              )}
      
              {/* ── Progress bar ── */}
              <div style={{
                marginTop:  validatedUserid && !validatedGroupid && !groupidSkipped ? 10 : 40,
                padding:    20,
                background: "rgba(255,255,255,0.03)",
                borderRadius: 8,
                border:     "1px solid rgba(255,255,255,0.1)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <span style={{ color: "white", fontSize: "0.9rem", fontWeight: 600 }}>
                    Progress: {validCount} of {groupidSkipped ? "1 (Group skipped)" : "2"}
                  </span>
                  {groupidSkipped && (
                    <span style={{
                      fontSize: "0.75rem", fontWeight: 600,
                      padding: "2px 8px", borderRadius: 20,
                      background: "rgba(255,152,0,0.15)",
                      border:     "1px solid rgba(255,152,0,0.3)",
                      color:      "#FF9800",
                    }}>
                      Group ID skipped
                    </span>
                  )}
                </div>
      
                {/* Bar */}
                <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.1)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{
                    height:     "100%",
                    width:      groupidSkipped
                      ? "100%"                           // full bar when skipped
                      : `${(validCount / 2) * 100}%`,
                    background: groupidSkipped
                      ? "linear-gradient(90deg, #2196F3, #FF9800)"   // orange tint when skipped
                      : "linear-gradient(90deg, #2196F3, #00BCD4)",
                    transition: "width 0.35s ease, background 0.35s ease",
                  }} />
                </div>
      
                {/* Helper text */}
                <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.78rem", marginTop: 10, marginBottom: 0 }}>
                  {groupidSkipped
                    ? "✅ Ready to continue — you can link a group later inside the post editor."
                    : validatedUserid && !validatedGroupid
                    ? "Provide a Group ID above, or skip to continue without one."
                    : !validatedUserid
                    ? "Enter your User ID to unlock step 2."
                    : "All required fields are complete."}
                </p>
              </div>
            </div>
          </div>
      
          <style>{`
            @keyframes spin  { to { transform: rotate(360deg); } }
            @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
          `}</style>
        </div>
      );
  }

  // ==================== MAIN FORM ====================

  return (
    <div style={mainContainerStyle}>
      <div style={pageContentStyle}>
        <div style={formContainerStyle}>

          {/* ── Title ── */}
          <h1 style={{ color: "white", fontSize: "2rem", marginBottom: 30, textAlign: "center" }}>Create Post</h1>

          {/* ── Validated IDs banner ── */}
          <div style={{ background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 10, padding: 15, marginBottom: 30, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", gap: 30, flex: 1 }}>
              {[
                { label: "User ID",  value: validatedUserid  },
                { label: "Group ID", value: validatedGroupid },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>{label}</div>
                  <div style={{ color: "#4CAF50", fontSize: "1rem", fontWeight: 600 }}>{value}</div>
                </div>
              ))}
            </div>
            <FaCheckCircle style={{ color: "#4CAF50", fontSize: "1.5rem" }} />
          </div>

          {/* ── Banners ── */}
          {submitSuccess && (
            <div style={{ background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", color: "#4CAF50", padding: 15, borderRadius: 8, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <FaCheckCircle /> <span>Post published successfully! Redirecting...</span>
            </div>
          )}
          {submitError && (
            <div style={{ background: "rgba(244,67,54,0.1)", border: "1px solid rgba(244,67,54,0.3)", color: "#f44336", padding: 15, borderRadius: 8, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
              <FaTimesCircle /> <span>{submitError}</span>
            </div>
          )}

          {/* ════════════════════════════════════════════
              ── SECTION: EVENT TYPE (MANDATORY) ────────
              ════════════════════════════════════════════ */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              <FaTag /> Event Type <span style={{ fontSize: "0.8rem", height: 100 }}>* required</span>
            </label>
            <select
              value={eventType}
              onChange={e => setEventType(e.target.value)}
              style={{
                ...selectStyle,
                border: !eventType
                  ? "1px solid rgba(244,67,54,0.5)"
                  : "1px solid rgba(76,175,80,0.4)",
              }}
            >
              <option value="" disabled style={{ background: "rgb(20,20,20)" }}>Select event type…</option>
              {EVENT_TYPES.map(t => (
                <option key={t.value} value={t.value} style={{ background: "rgb(20,20,20)" }}>{t.label}</option>
              ))}
            </select>
            {!eventType && (
              <div style={{ color: "#f44336", fontSize: "0.8rem", marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
                <FaInfo /> Event type is required to publish
              </div>
            )}
          </div>

          {/* ════════════════════════════════════════════
              ── SECTION: EVENT CATEGORY ─────────────────
              ════════════════════════════════════════════ */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              <FaLayerGroup /> Event Category <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", fontWeight: 400 }}>(optional)</span>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
              {EVENT_CATEGORIES.map(cat => {
                const active = eventCategory === cat.value;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setEventCategory(active ? "" : cat.value)}
                    style={{
                      padding: "12px 14px", borderRadius: 10, cursor: "pointer",
                      border: active ? "1px solid rgba(33,150,243,0.6)" : "1px solid rgba(255,255,255,0.1)",
                      background: active ? "rgba(33,150,243,0.15)" : "rgba(255,255,255,0.03)",
                      transition: "all 0.2s",
                      textAlign: "left",
                    }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                  >
                    <div style={{ color: active ? "#2196F3" : "white", fontWeight: 600, fontSize: "0.9rem", marginBottom: 3 }}>{cat.label}</div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", lineHeight: 1.3 }}>{cat.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ════════════════════════════════════════════
              ── SECTION: EVENT SEGMENT (OPTIONAL) ──────
              ════════════════════════════════════════════ */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              📅 Event Segment <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", fontWeight: 400 }}>(optional — which feed to appear in)</span>
            </label>
            <select
              value={eventSegment}
              onChange={e => setEventSegment(e.target.value)}
              style={selectStyle}
            >
              {EVENT_SEGMENTS.map(s => (
                <option key={s.value} value={s.value} style={{ background: "rgb(20,20,20)" }}>{s.label}</option>
              ))}
            </select>
          </div>

          {/* ════════════════════════════════════════════
              ── SECTION: VISIBILITY & AUDIENCE ─────────
              ════════════════════════════════════════════ */}
          <div style={{ ...sectionStyle, background: "rgba(255,255,255,0.02)", borderRadius: 12, padding: 20, border: "1px solid rgba(255,255,255,0.07)" }}>
            <div style={{ color: "white", fontWeight: 600, fontSize: "1rem", marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
              {isPrivate ? <FaLock style={{ color: "#FF9800" }} /> : <FaGlobe style={{ color: "#4CAF50" }} />}
              Visibility &amp; Audience
            </div>

            {/* ── Privacy toggle ── */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", marginBottom: 10 }}>Post visibility</div>
              <div style={{ display: "flex", gap: 10 }}>
                {[
                  { key: false, icon: <FaGlobe />,  label: "Public",  desc: "Visible to everyone",    color: "#4CAF50" },
                  { key: true,  icon: <FaLock />,   label: "Private", desc: "Visible to your groups only", color: "#FF9800" },
                ].map(opt => {
                  const active = isPrivate === opt.key;
                  return (
                    <button
                      key={String(opt.key)}
                      onClick={() => setIsPrivate(opt.key)}
                      style={{
                        flex: 1, padding: "14px 16px", borderRadius: 10, cursor: "pointer",
                        border: active ? `1px solid ${opt.color}55` : "1px solid rgba(255,255,255,0.1)",
                        background: active ? `${opt.color}18` : "rgba(255,255,255,0.03)",
                        display: "flex", alignItems: "center", gap: 10, transition: "all 0.2s",
                      }}
                    >
                      <span style={{ color: active ? opt.color : "rgba(255,255,255,0.4)", fontSize: "1.1rem" }}>{opt.icon}</span>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ color: active ? opt.color : "rgba(255,255,255,0.7)", fontWeight: 600, fontSize: "0.9rem" }}>{opt.label}</div>
                        <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.75rem" }}>{opt.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Society info ── */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ ...labelStyle, marginBottom: 8 }}>
                🏛️ Society / Organisation <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="text"
                value={societyInfo}
                onChange={e => setSocietyInfo(e.target.value)}
                placeholder="e.g. Mumbai Cultural Society, Tech Club…"
                style={{ ...inputStyle, width: "100%" }}
              />
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", marginTop: 6 }}>
                Associate this post with a society or organisation
              </div>
            </div>

            {/* ── Target groups ── */}
            <div>
              <label style={{ ...labelStyle, marginBottom: 8 }}>
                <FaUsers /> Share with specific groups <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem", fontWeight: 400 }}>(optional)</span>
              </label>

              {/* Tag chips */}
              {targetGroups.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                  {targetGroups.map(g => (
                    <div key={g} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "rgba(33,150,243,0.15)", border: "1px solid rgba(33,150,243,0.3)", color: "#2196F3", fontSize: "0.85rem", fontWeight: 500 }}>
                      {g}
                      <button
                        onClick={() => removeTargetGroup(g)}
                        style={{ background: "none", border: "none", color: "#2196F3", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", opacity: 0.7 }}
                      >
                        <FaTimes size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add group input */}
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  value={groupInput}
                  onChange={e => setGroupInput(e.target.value)}
                  placeholder="Enter group ID or name and press Add…"
                  style={{ ...inputStyle, flex: 1 }}
                  onKeyPress={e => e.key === "Enter" && addTargetGroup()}
                />
                <button
                  onClick={addTargetGroup}
                  style={{ ...submitBtnStyle, padding: "10px 18px", background: "rgba(33,150,243,0.2)", border: "1px solid rgba(33,150,243,0.4)", color: "#2196F3" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(33,150,243,0.3)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(33,150,243,0.2)"}
                >
                  + Add
                </button>
              </div>
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.8rem", marginTop: 6 }}>
                Add group IDs this post should be shared with (beyond the default group)
              </div>
            </div>
          </div>

          {/* ════════════════════════════════════════════
              ── SECTION: MEDIA UPLOAD ───────────────────
              ════════════════════════════════════════════ */}
          <div style={{ ...sectionStyle, marginTop: 30 }}>
            <div style={{ color: "white", fontSize: "1.1rem", fontWeight: 600, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <FaUpload /> Upload Media
            </div>

            <div
              style={{ border: "2px dashed rgba(255,255,255,0.3)", borderRadius: 12, padding: 30, textAlign: "center", cursor: "pointer", transition: "all 0.3s ease" }}
              onClick={() => fileInputRef.current?.click()}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(76,175,80,0.6)"; e.currentTarget.style.backgroundColor = "rgba(76,175,80,0.05)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.3)"; e.currentTarget.style.backgroundColor = "transparent"; }}
            >
              <div style={{ fontSize: "2.5rem", marginBottom: 10 }}>📁</div>
              <div style={{ color: "rgba(255,255,255,0.7)" }}>Click to upload or drag and drop</div>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: 5 }}>PNG, JPG, MP4 or other media formats</div>
            </div>

            <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleMediaUpload} style={{ display: "none" }} />

            {/* Upload progress list */}
            {mediaFiles.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ color: "white", fontSize: "0.95rem", fontWeight: 600, marginBottom: 15, display: "flex", alignItems: "center", gap: 8 }}>📊 Upload Progress</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                  {mediaFiles.map(media => (
                    <div key={media.id} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: 15 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                        <div style={{ color: "white", fontSize: "0.9rem", fontWeight: 500, flex: 1, wordBreak: "break-all" }}>{media.file.name}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 10 }}>
                          {media.uploadStatus === "success" && <div style={{ background: "#4CAF50", color: "white", padding: "4px 12px", borderRadius: 6, fontSize: "0.85rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}><FaCheck /> Uploaded</div>}
                          {media.uploadStatus === "error"   && <div style={{ background: "#f44336", color: "white", padding: "4px 12px", borderRadius: 6, fontSize: "0.85rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}><FaTimes /> Error</div>}
                        </div>
                      </div>
                      <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", marginBottom: 12 }}>
                        {formatBytes(media.uploadedBytes)} / {formatBytes(media.totalBytes)}
                      </div>
                      {media.uploadStatus === "uploading" && (
                        <>
                          <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.1)", borderRadius: 4, overflow: "hidden", marginBottom: 12 }}>
                            <div style={{ height: "100%", width: `${media.uploadProgress}%`, background: "linear-gradient(90deg, #2196F3, #00BCD4)", transition: "width 0.2s ease" }} />
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", marginBottom: 12 }}>
                            <span>{media.uploadProgress}%</span>
                            <span>Speed: {formatBytes(media.uploadSpeed)}/s</span>
                            <span>ETA: {formatTime(media.estimatedTimeRemaining)}</span>
                          </div>
                        </>
                      )}
                      {media.processingStatus && media.uploadStatus === "uploading" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#2196F3", fontSize: "0.85rem", marginBottom: 12, animation: "pulse 1.5s ease-in-out infinite" }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2196F3", animation: "pulse 1.5s ease-in-out infinite" }} />
                          Processing: {media.processingStatus.replace("-", " ").toUpperCase()}
                        </div>
                      )}
                      {media.uploadStatus === "success" && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#4CAF50", fontSize: "0.85rem" }}>
                          <FaCheck /> Processing complete
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview grid */}
            {mediaFiles.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 15, marginTop: 20 }}>
                {mediaFiles.map(media => (
                  <div key={media.id} style={{ position: "relative", borderRadius: 10, overflow: "hidden", aspectRatio: "1", border: media.uploadStatus === "success" ? "2px solid #4CAF50" : "none" }}>
                    {media.type === "image"
                      ? <img src={media.preview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: media.uploadStatus === "uploading" ? 0.5 : 1, transition: "opacity 0.3s ease" }} />
                      : <video src={media.preview}             style={{ width: "100%", height: "100%", objectFit: "cover", opacity: media.uploadStatus === "uploading" ? 0.5 : 1, transition: "opacity 0.3s ease" }} />
                    }
                    {media.uploadStatus !== "success" && (
                      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10 }}>
                        {media.uploadStatus === "uploading" && (
                          <>
                            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.3)", borderTop: "3px solid #2196F3", animation: "spin 1s linear infinite" }} />
                            <div style={{ color: "white", fontSize: "0.8rem", fontWeight: 600 }}>{media.uploadProgress}%</div>
                          </>
                        )}
                      </div>
                    )}
                    <div onClick={() => handleRemoveMedia(media.id)} style={{ position: "absolute", top: 5, right: 5, background: "#f44336", color: "white", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                      <FaTimes /> Remove
                    </div>
                    {media.uploadStatus === "success" && (
                      <div style={{ position: "absolute", bottom: 5, left: 5, background: "#4CAF50", color: "white", padding: "4px 8px", borderRadius: 4, fontSize: "0.75rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                        <FaCheck /> Ready
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Description ── */}
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, marginBottom: 8 }}>📝 Description</label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Write something about your post..." maxLength={500}
              style={{ ...inputStyle, width: "100%", minHeight: 120, resize: "vertical", fontFamily: "inherit" }}
            />
            <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginTop: 8 }}>{description.length} / 500 characters</div>
          </div>

          {/* ── Location ── */}
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, marginBottom: 8 }}><FaMapMarkerAlt /> Location</label>
            <input type="text" value={location} onChange={e => setLocation(e.target.value)} placeholder="Enter location (optional)" style={{ ...inputStyle, width: "100%" }} />
          </div>

          {/* ── Timestamp ── */}
          <div style={sectionStyle}>
            <label style={{ ...labelStyle, marginBottom: 8 }}><FaClock /> Date &amp; Time</label>
            <input type="datetime-local" value={timestamp} onChange={e => setTimestamp(e.target.value)} style={{ ...inputStyle, width: "100%", colorScheme: "dark" }} />
          </div>

          {/* ── Content Quality Meter ── */}
          <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: 15, marginBottom: 30 }}>
            <div style={{ color: "white", fontWeight: 600, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <FaInfo /> Content Quality: {contentQuality.overallScore}%
            </div>
            <div style={{ width: "100%", height: 8, background: "rgba(255,255,255,0.1)", borderRadius: 4, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${contentQuality.overallScore}%`, transition: "width 0.3s ease",
                background: `linear-gradient(90deg,
                  ${contentQuality.overallScore < 50 ? "#f44336" : contentQuality.overallScore < 80 ? "#ff9800" : "#4CAF50"},
                  ${contentQuality.overallScore < 50 ? "#d32f2f" : contentQuality.overallScore < 80 ? "#f57c00" : "#388e3c"})`,
              }} />
            </div>
            <div style={{ marginTop: 15 }}>
              {[
                { label: "Event type selected",           status: contentQuality.hasEventType        },
                { label: "Media uploaded",                status: contentQuality.hasMedia            },
                { label: "Description provided",          status: contentQuality.hasDescription      },
                { label: "Location provided",             status: contentQuality.hasLocation         },
                { label: "Media quality OK",              status: contentQuality.mediaQualityOk      },
                { label: "Description length sufficient", status: contentQuality.descriptionLengthOk },
              ].map((item, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, color: item.status ? "#4CAF50" : "rgba(255,255,255,0.5)", fontSize: "0.9rem", marginBottom: 8 }}>
                  {item.status ? <FaCheck /> : <FaTimes />}
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Quality Review Button ── */}
          <button
            onClick={() => setShowQualityReview(!showQualityReview)}
            disabled={!allUploadsComplete}
            style={{ width: "100%", padding: "14px", marginBottom: 20, borderRadius: 8, border: "none", background: allUploadsComplete ? "#2196F3" : "rgba(255,255,255,0.1)", color: allUploadsComplete ? "white" : "rgba(255,255,255,0.5)", fontSize: "1rem", fontWeight: "bold", cursor: allUploadsComplete ? "pointer" : "not-allowed", transition: "all 0.3s ease", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            onMouseEnter={e => { if (allUploadsComplete) { e.currentTarget.style.background = "#1976D2"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 15px rgba(33,150,243,0.3)"; } }}
            onMouseLeave={e => { e.currentTarget.style.background = allUploadsComplete ? "#2196F3" : "rgba(255,255,255,0.1)"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            <FaEye /> Review Content Quality
          </button>

          {/* ── Quality Review Panel (unchanged internals) ── */}
          {showQualityReview && allUploadsComplete && (
            <div style={{ background: "rgba(33,150,243,0.1)", border: "1px solid rgba(33,150,243,0.3)", borderRadius: 10, padding: 20, marginBottom: 30 }}>
              <h3 style={{ color: "#2196F3", fontSize: "1.1rem", fontWeight: 600, marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
                <FaEye /> Quality Review Details
              </h3>
              {qualityReviews.length === 0 ? (
                <div style={{ color: "rgba(255,255,255,0.6)", textAlign: "center", padding: 20 }}>Processing quality metrics...</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {qualityReviews.map((review, index) => (
                    <div key={review.mediaId} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: 15 }}>
                      <div style={{ color: "white", fontWeight: 600, marginBottom: 12 }}>Media #{index + 1}</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 15, marginBottom: 15 }}>
                        {[
                          { label: "Original Size",  value: formatBytes(review.originalSize),  color: "white",   accent: "#FF9800" },
                          { label: "Optimized Size", value: formatBytes(review.optimizedSize), color: "#4CAF50", accent: "#4CAF50" },
                        ].map(({ label, value, color, accent }) => (
                          <div key={label} style={{ background: "rgba(255,255,255,0.02)", padding: 10, borderRadius: 6, borderLeft: `3px solid ${accent}` }}>
                            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", marginBottom: 5 }}>{label}</div>
                            <div style={{ color, fontSize: "1.1rem", fontWeight: 600 }}>{value}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", borderRadius: 6, padding: 10, marginBottom: 15, color: "#4CAF50", fontSize: "0.9rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
                        <FaCheck />
                        {Math.round(((review.originalSize - review.optimizedSize) / review.originalSize) * 100)}% compression achieved
                      </div>
                      <div style={{ marginBottom: 15 }}>
                        <div style={{ color: "white", fontSize: "0.95rem", fontWeight: 600, marginBottom: 10 }}>Quality Metrics</div>
                        {[
                          { label: "Sharpness",      value: review.qualityMetrics.sharpness     },
                          { label: "Contrast",       value: review.qualityMetrics.contrast       },
                          { label: "Brightness",     value: review.qualityMetrics.brightness     },
                          { label: "Color Vibrancy", value: review.qualityMetrics.colorVibrancy  },
                        ].map(metric => (
                          <div key={metric.label} style={{ marginBottom: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, color: "rgba(255,255,255,0.7)", fontSize: "0.85rem" }}>
                              <span>{metric.label}</span>
                              <span style={{ fontWeight: 600, color: "white" }}>{metric.value}%</span>
                            </div>
                            <div style={{ width: "100%", height: 6, background: "rgba(255,255,255,0.1)", borderRadius: 3, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${metric.value}%`, transition: "width 0.3s ease", background: `linear-gradient(90deg, ${metric.value < 60 ? "#FF9800" : metric.value < 80 ? "#FFB74D" : "#4CAF50"}, ${metric.value < 60 ? "#F57C00" : metric.value < 80 ? "#FFA726" : "#388E3C"})` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <div style={{ color: "white", fontSize: "0.95rem", fontWeight: 600, marginBottom: 10 }}>Enhancements Applied</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {review.changes.map((change, idx) => (
                            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", padding: "8px 10px", background: "rgba(33,150,243,0.05)", borderRadius: 6, borderLeft: "3px solid #2196F3" }}>
                              <FaCheck style={{ color: "#2196F3", flexShrink: 0 }} /> <span>{change}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Submit Button ── */}
          <button
            onClick={handleSubmitPost}
            disabled={!canSubmit || isSubmitting}
            style={{ width: "100%", padding: "14px", borderRadius: 8, border: "none", background: canSubmit ? "#4CAF50" : "#555", color: "white", fontSize: "1rem", fontWeight: "bold", cursor: canSubmit ? "pointer" : "not-allowed", transition: "all 0.3s ease" }}
            onMouseEnter={e => { if (canSubmit) { e.currentTarget.style.background = "#45a049"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 15px rgba(76,175,80,0.3)"; } }}
            onMouseLeave={e => { e.currentTarget.style.background = canSubmit ? "#4CAF50" : "#555"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
            title={!eventType ? "Select an event type first" : !allUploadsComplete ? "Wait for all media to finish uploading" : "Submit your post"}
          >
            {isSubmitting ? "Publishing..." : "Publish Post"}
          </button>

          {/* Disabled reason hint */}
          {!canSubmit && !isSubmitting && (
            <div style={{ marginTop: 10, textAlign: "center", color: "rgba(255,255,255,0.35)", fontSize: "0.82rem" }}>
              {!eventType
                ? "⚠️ Event type is required"
                : !mediaFiles.length
                ? "⚠️ Upload at least one media file"
                : !allUploadsComplete
                ? "⏳ Waiting for uploads to complete"
                : "⚠️ You must be a group member to post"}
            </div>
          )}

        </div>
      </div>

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
        select option { background: rgb(20,20,20) !important; color: white !important; }
      `}</style>
    </div>
  );
};

export default PostContentPage;